<!--
 @description Slide-in panel that animates from the right (default) or the
 left. Closes on an outside click or ESC when open, and optionally shows a
 close icon. Emits `close` when dismissed — the parent controls the open state
 via `isOpen`. Use as a container for navigation or settings that should not
 block the full page.

 @props
   isClosable — When true (default), a close icon (×) is rendered inside the
                panel. Set to false to suppress the icon when a parent provides
                its own close trigger.
   isOpen     — Controls panel visibility. The parent is responsible for
                toggling this value in response to the `close` event.
   leftMenu   — When true, the panel slides in from the left instead of the
                right and the close icon is repositioned accordingly.

 @emits
   close — Emitted when the panel is dismissed (outside click, ESC, or the
           close icon tap).

 @exposes
   closeButtonEl — Template ref pointing to the close <button> element, so the
                   parent can move focus into it when the panel opens (AC-09).

 @example
 <SideMenu ref="sideMenuRef" :isOpen="menuOpen" @close="menuOpen = false">
   <nav>...</nav>
 </SideMenu>

 @accessibility
   - ESC closes the panel (keydown handler on document while panel is open).
   - Focus is moved to the close button by the parent via the exposed ref.
   - Tab order follows DOM source order (top-to-bottom through slot content).
-->
<template>
  <div ref="sideMenuContainerEl">
    <transition :name="leftMenu ? 'slider-left' : 'slider'">
      <div class="side-menu__panel" :class="{ 'side-menu__panel--left': leftMenu }" v-show="isOpen">
        <button
          v-show="isClosable"
          ref="closeButtonEl"
          type="button"
          class="side-menu__close-btn"
          :class="{
            'side-menu__close-btn--left': leftMenu,
            'side-menu__close-btn--right': !leftMenu
          }"
          :aria-label="t('game.sideMenu.aria.close')"
          @click.stop="close"
        >
          <i class="fi fi-br-cross" aria-hidden="true"></i>
        </button>
        <slot />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    /** When true, a close icon (×) is rendered inside the panel. */
    isClosable?: boolean
    /** Controls panel visibility. */
    isOpen?: boolean
    /** When true, the panel slides in from the left instead of the right. */
    leftMenu?: boolean
  }>(),
  {
    isClosable: true,
    isOpen: false,
    leftMenu: false
  }
)

const sideMenuContainerEl = ref<HTMLElement | undefined>()

/** Exposed so the parent can focus the close button when the panel opens (AC-09). */
const closeButtonEl = ref<HTMLButtonElement | undefined>()

const emit = defineEmits<{
  close: []
}>()

defineExpose({ closeButtonEl })

function checkClickPosition(e: MouseEvent) {
  if (e.target === null) return
  if (
    props.isOpen &&
    sideMenuContainerEl.value &&
    !sideMenuContainerEl.value.contains(e.target as Node)
  ) {
    close()
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.isOpen) {
    close()
  }
}

function close() {
  emit('close')
}

onMounted(() => {
  document.addEventListener('mousedown', checkClickPosition)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', checkClickPosition)
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Block: side-menu
// The panel root carries side-menu__panel; the wrapper div is unstyled.
// ---------------------------------------------------------------------------

.side-menu {
  &__panel {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: fixed;
    width: 100%;
    max-width: 250px;
    height: 100vh;
    background-color: var(--background-light);
    top: 0;
    right: 0;
    z-index: 99;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

    li {
      margin-left: 0;
    }

    a {
      margin: 15px 0;
      width: 85%;

      img {
        width: 100%;
      }
    }

    // RESP-INFO-02: align to the project's md breakpoint token (720px).
    // The original 768px had no documented rationale; _breakpoints.scss defines md: 720px.
    @include breakpoint-md {
      max-width: 60vw;
    }

    &--left {
      left: 0;
    }
  }

  &__close-btn {
    background-color: transparent;
    width: fit-content;
    padding: 0;

    &:hover {
      background-color: transparent;
    }

    // Right-side panel: close icon pinned to the top-left of the panel.
    &--right {
      position: absolute;
      z-index: 99;
      top: 0.9rem;
      left: 1rem;
      font-size: 1rem;
      color: var(--text);
      transition: none;
      cursor: pointer;

      &:hover {
        color: var(--primary);
      }
    }

    // Left-side panel: close icon pinned to the top-right of the panel.
    &--left {
      position: absolute;
      z-index: 99;
      top: 0.9rem;
      right: 1rem;
      font-size: 1rem;
      color: var(--text);
      transition: none;
      cursor: pointer;

      &:hover {
        color: var(--primary);
      }
    }
  }
}

.slider-enter-active,
.slider-leave-active {
  transition: all 0.5s ease;
}

.slider-enter-from,
.slider-leave-to {
  transform: translateX(250px);
}

.slider-enter-to {
  transform: translateX(0);
}

.slider-left-enter-active,
.slider-left-leave-active {
  transition: all 0.5s ease;
}

.slider-left-enter-from,
.slider-left-leave-to {
  transform: translateX(-250px);
}

.slider-left-enter-to {
  transform: translateX(0);
}

// RESP-03: replace the slide motion with an opacity fade for users who have
// enabled "Reduce Motion" in their OS (WCAG 2.3.3 — Animation from Interactions).
// We keep visibility intact; only the translateX displacement is removed.
@media (prefers-reduced-motion: reduce) {
  .slider-enter-active,
  .slider-leave-active,
  .slider-left-enter-active,
  .slider-left-leave-active {
    transition: opacity 0.15s ease;
  }

  .slider-enter-from,
  .slider-leave-to,
  .slider-left-enter-from,
  .slider-left-leave-to {
    transform: none;
    opacity: 0;
  }
}
</style>
